import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../pages/components/Navbar";
import Footer from "../pages/components/Footer";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import { DynamicSimpleProductCard } from "./components/ProductCard";
import FloatingWidgets from "./components/FloatingWidgets";
import SeoHelmet from "./components/SeoHelmet";

const normalizeImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
};

const formatPrice = (value) => `Rs. ${Math.round(Number(value || 0))}`;

const getSellingPrice = (item) => Number(item?.price || 0);
const getStrikePrice = (item) => Number(item?.mrp || 0);

const parseWeightToGrams = (weight) => {
  const value = String(weight || "")
    .toLowerCase()
    .trim();
  if (value.endsWith("kg")) {
    return Number(value.replace("kg", "").trim()) * 1000;
  }
  if (value.endsWith("g")) {
    return Number(value.replace("g", "").trim());
  }
  return 0;
};

const formatWeight = (grams) => {
  if (grams >= 1000) return `${grams / 1000}kg`;
  return `${grams}g`;
};

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const discountPercent = (price, oldPrice) => {
  if (!oldPrice || oldPrice <= price) return 0;
  return Math.round(((oldPrice - price) / oldPrice) * 100);
};

export default function ProductDetails() {
  const { slug } = useParams();
  const { addToCart } = useAppContext();

  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [selectedWeight, setSelectedWeight] = useState(""); // Initialize with empty string
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [isSoldOut, setIsSoldOut] = useState(false); // New state for overall stock

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        window.scrollTo({ top: 0, behavior: "instant" });
        setLoading(true);
        setError("");

        const res = await fetch(
          `${API_BASE_URL}/api/content/products/slug/${slug}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const result = await res.json();
        const productData = result?.data;

        if (!productData) {
          setError("Product not found");
          return;
        }

        let availableWeightOptions = [];
        let productIsSoldOut = true;

        if (productData.variants && productData.variants.length > 0) {
          availableWeightOptions = productData.variants.map((variant) => ({
            weight: variant.weight,
            mrp: variant.mrp,
            price: variant.price,
            stock: Number(variant.stock || 0),
          }));

          if (availableWeightOptions.length > 0) {
            productIsSoldOut = false;
          }
        } else {
          // Fallback for products without variants or if variants are empty
          const basePrice = getSellingPrice(productData);
          const baseMrp = getStrikePrice(productData);
          const baseWeightGrams = productData.isCombo
            ? 1000
            : parseWeightToGrams(
                productData.weight || productData.net_wt || "500g",
              );

          if (baseWeightGrams > 0) {
            const optionsInGrams = [250, 500, 1000].filter(
              (g) => g <= baseWeightGrams,
            );
            availableWeightOptions = optionsInGrams.map((grams) => {
              const ratio = grams / baseWeightGrams;
              return {
                weight: formatWeight(grams),
                price: Math.round(basePrice * ratio),
                mrp: Math.round(baseMrp * ratio),
                stock: productData.stock || 1, // Assume stock from top-level if no variants
              };
            });
          }
          productIsSoldOut = (productData.stock || 0) <= 0;
        }

        // Determine if overall product is sold out across all options
        if (availableWeightOptions.some((opt) => opt.stock > 0)) {
          productIsSoldOut = false;
        }

        setProduct({ ...productData, weightOptions: availableWeightOptions });
        setIsSoldOut(productIsSoldOut);

        if (productData?.category && typeof productData.category === "object") {
          setCategory(productData.category);
        }

        const defaultWeight =
          availableWeightOptions.find((o) => o.stock > 0)?.weight ||
          (availableWeightOptions.length > 0
            ? availableWeightOptions[0].weight
            : "");

        setSelectedWeight(defaultWeight);

        const allImages = [
          normalizeImage(productData.mainImage),
          ...(Array.isArray(productData.gallery)
            ? productData.gallery.map(normalizeImage)
            : []),
        ].filter(Boolean);

        setActiveImage(allImages[0] || "");
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Unable to load product");
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchProduct();
  }, [slug]);

  useEffect(() => {
    const fetchRelated = async () => {
      let endpoint = "";
      // If viewing a combo, show other combos. Otherwise, show same category items.
      if (product?.isCombo) {
        endpoint = `${API_BASE_URL}/api/content/combos?limit=5&status=Published`;
      } else if (product?.category) {
        const catId =
          typeof product.category === "object"
            ? product.category._id
            : product.category;
        endpoint = `${API_BASE_URL}/api/content/products?category=${catId}&limit=5&status=Published`;
      }

      if (!endpoint) return;

      try {
        const res = await fetch(endpoint);
        const result = await res.json();
        const list = Array.isArray(result?.data) ? result.data : [];

        const filtered = list
          .filter((p) => (p.slug || p._id) !== slug)
          .slice(0, 4)
          .map((p) => ({
            ...p,
            img: normalizeImage(p.mainImage || p.image || p.img),
            categoryName:
              p.category?.name ||
              (product?.isCombo ? "Combos" : category?.name || "Products"),
          }));

        setRelatedProducts(filtered);
      } catch (err) {
        console.error("Error fetching related products:", err);
      }
    };

    if (product) fetchRelated();
  }, [product, slug, category]);

  const images = useMemo(() => {
    if (!product) return [];
    return [
      normalizeImage(product.mainImage),
      ...(Array.isArray(product.gallery)
        ? product.gallery.map(normalizeImage)
        : []),
    ].filter(Boolean);
  }, [product]);

  const weights = useMemo(() => {
    if (!product) return [];
    return product.weightOptions?.length
      ? product.weightOptions.map((o) => o.weight)
      : [product.net_wt || product.netWt || product.weight || "500g"];
  }, [product]);

  const currentOption = useMemo(() => {
    if (!product?.weightOptions) return null;
    return (
      product.weightOptions.find((o) => o.weight === selectedWeight) ||
      product.weightOptions[0]
    );
  }, [product, selectedWeight]);

  const finalPrice = useMemo(
    () => (currentOption ? currentOption.price : getSellingPrice(product)),
    [currentOption, product],
  );

  const oldPrice = useMemo(
    () => (currentOption ? currentOption.mrp : getStrikePrice(product)),
    [currentOption, product],
  );

  const offPercent = useMemo(
    () => discountPercent(finalPrice, oldPrice),
    [finalPrice, oldPrice],
  );

  const showSuccessMessage = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.style.display = "flex";
      setTimeout(() => {
        element.style.display = "none";
      }, 2000);
    }
  };

  const handleAddToCart = () => {
    if (!product || !currentOption || currentOption.stock <= 0) return;

    const newItem = {
      productId: product._id || product.slug,
      slug: product.slug,
      name: product.name,
      img: normalizeImage(product.mainImage),
      price: finalPrice,
      quantity,
      net_wt: selectedWeight,
      old_price: oldPrice,
      categoryName: category?.name || "Catalog",
    };

    addToCart(newItem, false);

    showSuccessMessage(".added-to-cart");

    const cartOffcanvas = document.getElementById("cartmini");
    if (cartOffcanvas && window.bootstrap && window.bootstrap.Offcanvas) {
      const bsOffcanvas =
        window.bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvas);
      bsOffcanvas.show();
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: product?.name,
      text: product?.shortDescription || "Check this product",
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // fallback if Web Share API not supported
        navigator.clipboard.writeText(window.location.href);
        alert("Link copied!");
      }
    } catch (err) {
      console.log("Share cancelled or failed", err);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div
          className="container py-80 text-center"
          style={{ minHeight: "60vh" }}
        >
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SeoHelmet
        title={product?.metaTitle || `${product?.name} | A1 Bakery & Sweets`}
        description={
          product?.metaDescription ||
          product?.shortDescription ||
          product?.description
        }
      />
      <Navbar />
      <div className="container pt-30">
        <div className="breadcrumb__list mb-30">
          <span>
            <Link to="/">Home</Link>
          </span>
          <span className="dvdr">
            <i className="fa-regular fa-angle-right"></i>
          </span>
          <span>
            <Link to="/categories">Shop</Link>
          </span>
          {category && (
            <>
              <span className="dvdr">
                <i className="fa-regular fa-angle-right"></i>
              </span>
              <span>
                <Link
                  to={`/categories/${category.slug || slugify(category.name)}`}
                >
                  {category.name}
                </Link>
              </span>
            </>
          )}
          <span className="dvdr">
            <i className="fa-regular fa-angle-right"></i>
          </span>
          <span>{product.name}</span>
        </div>
        <div className="row g-4">
          {/* LEFT: 6 COLUMNS (Visuals) */}
          <div className="col-lg-6">
            <div className="visual-stage-5">
              <div
                className="main-image-frame mb-4"
                style={{
                  position: "relative",
                  border: "1px solid #f0f0f0",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={activeImage}
                  className="img-fluid w-100"
                  alt={product.name}
                />
                {isSoldOut && (
                  <span className="badge bg-danger position-absolute top-0 start-0 m-2">
                    Sold Out
                  </span>
                )}
              </div>

              <div className="d-flex gap-2">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    style={{
                      width: "80px",
                      height: "80px",
                      cursor: "pointer",
                      border:
                        activeImage === img
                          ? "1px solid var(--clr-custom-2)"
                          : "1px solid #eee",
                      opacity: activeImage === img ? 1 : 0.6,
                    }}
                  >
                    <img
                      src={img}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "cover" }}
                      alt="thumb"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: 6 COLUMNS (Content) */}
          <div className="col-lg-6">
            <div className="details-stage-7 ps-lg-5">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <span className="text-uppercase small fw-bold text-muted mb-1 d-block">
                    {category?.name || "Catalog"}
                  </span>
                  <h1 className="product-title-slim fs-1 mb-0">
                    {product.name}
                  </h1>
                </div>
                <div className="d-flex gap-2 pt-2">
                  <button
                    onClick={handleShare}
                    className="btn border rounded-circle"
                    title="Share Product"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <path d="M8.59 13.51l6.83 3.98" />
                      <path d="M15.41 6.51l-6.82 3.98" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 mb-2">
                <span
                  className="fs-4 fw-bold"
                  style={{ color: "var(--clr-custom-2)" }}
                >
                  {formatPrice(finalPrice)}
                </span>
                {oldPrice > 0 && (
                  <del className="medium text-muted">
                    {formatPrice(oldPrice)}
                  </del>
                )}
                {offPercent > 0 && (
                  <span
                    className="badge bg-danger bg-opacity-10 text-white border border-danger-subtle rounded-pill px-2 py-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {offPercent}% OFF
                  </span>
                )}
              </div>

              {/* Stock Status Row */}
              <div className="mb-3">
                {currentOption ? (
                  currentOption.stock > 0 ? (
                    <span
                      className={`fw-bold small ${currentOption.stock < 20 ? "text-warning" : "text-success"}`}
                    >
                      <i
                        className="fas fa-circle me-2"
                        style={{ fontSize: "8px", verticalAlign: "middle" }}
                      ></i>
                      {currentOption.stock < 20 ? "Low Stock" : "In Stock"} :{" "}
                      {currentOption.stock} available
                    </span>
                  ) : (
                    <span className="text-danger fw-bold small">
                      <i
                        className="fas fa-circle me-2"
                        style={{ fontSize: "8px", verticalAlign: "middle" }}
                      ></i>
                      Out of Stock
                    </span>
                  )
                ) : isSoldOut ? (
                  <span className="text-danger fw-bold small">
                    <i
                      className="fas fa-circle me-2"
                      style={{ fontSize: "8px", verticalAlign: "middle" }}
                    ></i>
                    Sold Out
                  </span>
                ) : null}
              </div>

              <div
                className="text-secondary mb-3"
                style={{ fontSize: "1.05rem", lineHeight: "1.7" }}
              >
                {product.isCombo && (
                  <div className="mb-3">
                    <span className="badge bg-danger mb-2">Combo Pack</span>
                    <p className="fw-bold text-dark">
                      This combo contains{" "}
                      {product.items || product.productsCount} signature items.
                    </p>
                    {product.products && product.products.length > 0 && (
                      <div className="combo-contents-list mt-3 p-3 bg-light rounded-3 border">
                        <h6 className="fw-bold small text-uppercase mb-2">
                          What's inside:
                        </h6>
                        <ul className="list-unstyled mb-0">
                          {product.products.map((item, idx) => (
                            <li
                              key={idx}
                              className="small d-flex align-items-center gap-2 mb-1"
                            >
                              <i
                                className="fas fa-check-circle text-success"
                                style={{ fontSize: "10px" }}
                              ></i>
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {product.shortDescription || product.description}
              </div>

              <hr className="my-3" style={{ opacity: "0.1" }} />

              {/* Selection Section */}
              <div className="mb-4">
                <span className="d-block small fw-bold mb-3 text-uppercase">
                  Size / Weight
                </span>
                <div className="d-flex flex-wrap gap-2">
                  {weights.map((w, i) => {
                    const opt = product.weightOptions?.find(
                      (o) => o.weight === w,
                    );
                    const isAvailable = opt ? opt.stock > 0 : true;
                    const isLow = opt && opt.stock > 0 && opt.stock < 20;

                    return (
                      <button
                        key={i}
                        className={`weight-chip-minimal ${selectedWeight === w ? "is-active" : ""} ${!isAvailable ? "opacity-50" : ""}`}
                        onClick={() => isAvailable && setSelectedWeight(w)}
                        disabled={!isAvailable}
                        style={{
                          cursor: isAvailable ? "pointer" : "not-allowed",
                        }}
                      >
                        {w}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action Bar */}
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="qty-pill">
                  <button
                    className="qty-btn-tiny"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={isSoldOut || currentOption?.stock <= 0}
                  >
                    —
                  </button>
                  <input
                    type="text"
                    className="text-center border-0 bg-transparent small fw-bold"
                    style={{ width: "30px" }}
                    value={quantity}
                    readOnly
                  />
                  <button
                    className="qty-btn-tiny"
                    onClick={() => setQuantity((q) => q + 1)}
                    disabled={isSoldOut || currentOption?.stock <= 0}
                  >
                    +
                  </button>
                </div>

                <button
                  className="btn-slim-primary flex-grow-1 py-3"
                  onClick={handleAddToCart}
                  disabled={isSoldOut || currentOption?.stock <= 0}
                >
                  {isSoldOut || currentOption?.stock <= 0
                    ? "Sold Out"
                    : "Add to Cart"}
                </button>
              </div>

              {/* NEW: Free Delivery Box */}
              <div className="p-3 mb-4 rounded-3 border border-success bg-success-subtle d-flex align-items-center gap-3">
                <i className="fa-solid fa-truck-fast text-success fs-4"></i>
                <div>
                  <p className="fw-bold text-success m-0">Free Delivery</p>
                  <p className="small text-muted m-0">
                    On order above Rs. 1000/-
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <section className="related-products-area py-3">
          <div className="container">
            <div className="d-flex justify-content-between align-items-end mb-4">
              <div>
                <h2 className="bd-section__title mb-0">
                  {product?.isCombo
                    ? "Other Exciting Combos"
                    : "Similar Delights"}
                </h2>
                <p className="">You might also enjoy these fresh treats</p>
              </div>
              <Link
                to="/categories"
                className="text-danger fw-bold text-decoration-none"
              >
                View All <i className="fal fa-angle-right"></i>
              </Link>
            </div>
            <div className="row">
              {relatedProducts.map((item) => (
                <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={item._id}>
                  <DynamicSimpleProductCard product={item} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="container my-5 py-2 p-md-5 bg-light rounded-3">
        <div className="row align-items-center">
          <div className="col-md-6 col-12 mb-4 mb-md-0">
            <img
              src="/assets/img/a1-img/about__us/video_cont.png"
              className="img-fluid"
              alt=""
            />
          </div>
          <div className="col-md-6 col-12">
            <div className="p-3 p-md-5 text-center text-md-start">
              <h2 className="bd-section__title">Explore Our Collections</h2>
              <p className="mb-4 mt-4">
                We are committed to providing you with the best experience. We
                use only the finest ingredients, sourced from trusted suppliers,
                to create our delicious ghee-based products. Our recipes have
                been perfected over 35+ years, ensuring that every bite is a
                taste of tradition and quality.
              </p>
              <Link
                to="/categories"
                className="btn-slim-primary py-3 px-5 mt-3"
              >
                Explore
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="feature-section pb-80 pt-40">
        <div className="feature-container">
          <div className="feature-item">
            <img
              src="/assets/img/a1-img/dash__elements/fe-1.png"
              className="feature-icon"
              alt=""
            />
            <div className="feature-title">Pure Ingredients</div>
            <div className="feature-desc">
              We use only pure ghee and farm-fresh ingredients.
            </div>
          </div>
          <div className="feature-item">
            <img
              src="/assets/img/a1-img/dash__elements/fe-3.png"
              className="feature-icon"
              alt=""
            />
            <div className="feature-title">Nutritional Conscious</div>
            <div className="feature-desc">
              Balanced recipes that focus on health and taste.
            </div>
          </div>
          <div className="feature-item">
            <img
              src="/assets/img/a1-img/dash__elements/fe-4.png"
              className="feature-icon"
              alt=""
            />
            <div className="feature-title">Legacy of Taste</div>
            <div className="feature-desc">
              Each creation follows our 35+ years secret recipes.
            </div>
          </div>
          <div className="feature-item">
            <img
              src="/assets/img/a1-img/dash__elements/fe-2.png"
              className="feature-icon"
              alt=""
            />
            <div className="feature-title">New Creations, Every Year</div>
            <div className="feature-desc">
              Each Year, We Introduce A New Product.
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWidgets />
    </>
  );
}
